import React, { createContext, useContext, ReactNode } from 'react'
import './SnackbarProvider.css'

// Types pour les notifications snackbar
interface SnackbarMessage {
  id: string
  message: string
  type?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

interface SnackbarContextType {
  showSnackbar: (message: string, type?: SnackbarMessage['type'], duration?: number) => void
  showSuccess: (message: string, duration?: number) => void
  showError: (message: string, duration?: number) => void
  showWarning: (message: string, duration?: number) => void
  showInfo: (message: string, duration?: number) => void
  hideSnackbar: (id: string) => void
  clearAll: () => void
  snackbars: SnackbarMessage[]
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

export const useSnackbar = () => {
  const context = useContext(SnackbarContext)
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider')
  }
  return context
}

interface SnackbarProviderProps {
  children: ReactNode
  defaultDuration?: number
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({
  children,
  defaultDuration = 5000,
}) => {
  const [snackbars, setSnackbars] = React.useState<SnackbarMessage[]>([])

  const generateId = () => Date.now().toString() + Math.random().toString(36).substr(2, 9)

  const addSnackbar = (message: string, type: SnackbarMessage['type'] = 'info', duration?: number) => {
    const id = generateId()
    const newSnackbar: SnackbarMessage = {
      id,
      message,
      type,
      duration: duration || defaultDuration
    }

    setSnackbars(prev => {
      const next = [...prev, newSnackbar]
      return next.length > 5 ? next.slice(next.length - 5) : next
    })

    // Auto-hide après la durée spécifiée
    const hideDuration = duration || defaultDuration
    setTimeout(() => {
      hideSnackbar(id)
    }, hideDuration)

    return id
  }

  const showSnackbar = (message: string, type?: SnackbarMessage['type'], duration?: number) => {
    return addSnackbar(message, type, duration)
  }

  const showSuccess = (message: string, duration?: number) => {
    return addSnackbar(message, 'success', duration)
  }

  const showError = (message: string, duration?: number) => {
    return addSnackbar(message, 'error', duration)
  }

  const showWarning = (message: string, duration?: number) => {
    return addSnackbar(message, 'warning', duration)
  }

  const showInfo = (message: string, duration?: number) => {
    return addSnackbar(message, 'info', duration)
  }

  const hideSnackbar = (id: string) => {
    setSnackbars(prev => prev.filter(snackbar => snackbar.id !== id))
  }

  const clearAll = () => {
    setSnackbars([])
  }

  const value: SnackbarContextType = {
    showSnackbar,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideSnackbar,
    clearAll,
    snackbars,
  }

  return (
    <SnackbarContext.Provider value={value}>
      {children}

      {/* Conteneur pour les snackbars */}
      <div className="snackbar-container">
        {snackbars.map((snackbar) => (
          <div
            key={snackbar.id}
            className={`snackbar snackbar-${snackbar.type || 'info'}`}
          >
            <div className="snackbar-content">
              <span className="snackbar-message">{snackbar.message}</span>
              {snackbar.action && (
                <button
                  className="snackbar-action"
                  onClick={snackbar.action.onClick}
                >
                  {snackbar.action.label}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </SnackbarContext.Provider>
  )
}
